"use client";

import { Section } from "@astryxdesign/core/Section";
import { Center } from "@astryxdesign/core/Center";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { Icon } from "@astryxdesign/core/Icon";
import { LandingCanvasText } from "@/components/landing/landing-canvas-text";
import { LandingCmsImage } from "@/components/landing/landing-cms-image";
import { ResearchIcon } from "@/components/icons/research-icon";
import { BRANDBOOK } from "@/lib/brand/brandbook";
import { LANDING_SECTION_MAX } from "@/lib/landing/layout";
import { normalizeBackgroundImageUrl } from "@/lib/landing/media/background-image";
import {
  DEFAULT_HERO_SPLIT,
  type LandingHeroSplitHighlight,
} from "@/lib/landing/puck/hero-split-defaults";
import { parseLandingIcon } from "@/lib/landing/puck/icon-options";
import {
  landingTextProps,
  normalizeBackgroundColor,
  styledTextString,
  styledTextStyle,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";
import { researchIcons, type ResearchIconName } from "@/lib/icons/research-icons";
import {
  landingBody,
  landingButtonText,
  landingFont,
  landingH1,
} from "@/lib/landing/brandbook-ui";

export type LandingHeroSplitViewProps = {
  sectionId?: string;
  backgroundColor?: string;
  backgroundColorDark?: string;
  title?: LandingTextValue;
  subtitle?: LandingTextValue;
  primaryLabel?: LandingTextValue;
  primaryHref?: string;
  secondaryLabel?: LandingTextValue;
  secondaryHref?: string;
  imageUrl?: string;
  imageUrlDark?: string;
  imageAlt?: string;
  showHighlights?: boolean;
  highlights?: LandingHeroSplitHighlight[];
};

function resolveImageUrl(value: unknown): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  const absolute = normalizeBackgroundImageUrl(trimmed);
  if (absolute) return absolute;
  if (trimmed.startsWith("/")) return trimmed;
  return "";
}

function HeroSplitHighlights({ items }: { items: LandingHeroSplitHighlight[] }) {
  if (items.length === 0) return null;

  return (
    <div className="mt-12 w-full border-t border-[color-mix(in_srgb,var(--color-text-primary)_14%,transparent)] pt-8 md:mt-14 md:pt-9">
      <ul className="m-0 grid w-full list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0">
        {items.map((item, index) => {
          const iconName = parseLandingIcon(item.icon) as ResearchIconName;
          const title = styledTextString(item.title);
          const text = styledTextString(item.text);
          return (
            <li
              key={`${iconName}-${title}-${index}`}
              className={[
                "flex items-start gap-3",
                index === 0
                  ? "lg:pr-7"
                  : "lg:border-l lg:border-[color-mix(in_srgb,var(--color-text-primary)_14%,transparent)] lg:px-7",
              ].join(" ")}
            >
              <span
                className="mt-0.5 inline-flex shrink-0 text-[var(--landing-accent-deep,#E11D48)]"
                aria-hidden
              >
                <ResearchIcon name={iconName} size="md" />
              </span>
              <div
                className={`${landingFont} min-w-0 text-[15px] leading-[1.35] text-[var(--color-text-primary)]`}
              >
                <LandingCanvasText
                  field={`highlights.${index}.title`}
                  value={item.title}
                  label="Строка 1"
                  hideIfEmpty
                >
                  <span className="font-semibold" {...landingTextProps(item.title)} />
                </LandingCanvasText>
                {text ? (
                  <>
                    {" "}
                    <LandingCanvasText
                      field={`highlights.${index}.text`}
                      value={item.text}
                      label="Строка 2"
                      hideIfEmpty
                    >
                      <span className="font-normal" {...landingTextProps(item.text)} />
                    </LandingCanvasText>
                  </>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Hero: текст слева, слот под S3-картинку справа, полоса преимуществ снизу.
 * Декор (красные полоски и т.п.) — в загружаемом PNG, не в CSS.
 */
export function LandingHeroSplitView({
  sectionId = DEFAULT_HERO_SPLIT.sectionId,
  backgroundColor = DEFAULT_HERO_SPLIT.backgroundColor,
  backgroundColorDark = DEFAULT_HERO_SPLIT.backgroundColorDark,
  title = DEFAULT_HERO_SPLIT.title,
  subtitle = DEFAULT_HERO_SPLIT.subtitle,
  primaryLabel = DEFAULT_HERO_SPLIT.primaryLabel,
  primaryHref = DEFAULT_HERO_SPLIT.primaryHref,
  secondaryLabel = DEFAULT_HERO_SPLIT.secondaryLabel,
  secondaryHref = DEFAULT_HERO_SPLIT.secondaryHref,
  imageUrl = DEFAULT_HERO_SPLIT.imageUrl,
  imageUrlDark = DEFAULT_HERO_SPLIT.imageUrlDark,
  imageAlt = DEFAULT_HERO_SPLIT.imageAlt,
  showHighlights = DEFAULT_HERO_SPLIT.showHighlights,
  highlights = DEFAULT_HERO_SPLIT.highlights,
}: LandingHeroSplitViewProps) {
  const lightUrl = resolveImageUrl(imageUrl);
  const darkUrl = resolveImageUrl(imageUrlDark) || lightUrl;
  const hasImage = Boolean(lightUrl);
  const hasSeparateDark = Boolean(darkUrl) && darkUrl !== lightUrl;
  const primaryText = styledTextString(primaryLabel);
  const secondaryText = styledTextString(secondaryLabel);
  const alt = imageAlt?.trim() || styledTextString(title) || "Hero";
  const items =
    Array.isArray(highlights) && highlights.length > 0
      ? highlights
      : DEFAULT_HERO_SPLIT.highlights;

  const lightBg =
    normalizeBackgroundColor(backgroundColor) || DEFAULT_HERO_SPLIT.backgroundColor;
  const darkBg =
    normalizeBackgroundColor(backgroundColorDark) ||
    DEFAULT_HERO_SPLIT.backgroundColorDark ||
    BRANDBOOK.ink;

  return (
    <div className="landing-hero-split relative">
      <div
        aria-hidden
        className="landing-hero-split__bg landing-hero-split__bg--light pointer-events-none absolute inset-0"
        style={{ backgroundColor: lightBg }}
      />
      <div
        aria-hidden
        className="landing-hero-split__bg landing-hero-split__bg--dark pointer-events-none absolute inset-0"
        style={{ backgroundColor: darkBg }}
      />

      <Section
        id={sectionId}
        variant="transparent"
        paddingBlock={0}
        className="relative z-[1] bg-transparent"
      >
        <Center>
          <div
            className="mx-auto w-full px-6 py-14 md:py-16 lg:py-[4.5rem]"
            style={{ maxWidth: LANDING_SECTION_MAX }}
          >
            <div className="grid w-full grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-8 xl:gap-12">
              <VStack gap={6} align="start" width="100%" className="min-w-0 lg:max-w-[34rem]">
                <LandingCanvasText
                  field="title"
                  value={title}
                  label="Заголовок"
                  multiline
                >
                  <Heading
                    level={1}
                    className={`${landingH1} text-balance`}
                    {...landingTextProps(title)}
                  />
                </LandingCanvasText>

                <LandingCanvasText
                  field="subtitle"
                  value={subtitle}
                  label="Подзаголовок"
                  multiline
                  hideIfEmpty
                >
                  <Text
                    className={`${landingBody} max-w-[36rem] text-pretty`}
                    {...landingTextProps(subtitle)}
                  />
                </LandingCanvasText>

                <HStack gap={3} wrap="wrap" className="pt-1">
                  <LandingCanvasText
                    field="primaryLabel"
                    value={primaryLabel}
                    label="Основная кнопка"
                    inline
                  >
                    <Button
                      label={primaryText}
                      variant="primary"
                      size="lg"
                      href={primaryHref}
                      className={landingButtonText}
                      style={styledTextStyle(primaryLabel)}
                      endContent={
                        <Icon
                          icon={researchIcons.chevronRight}
                          size="sm"
                          color="inherit"
                        />
                      }
                    />
                  </LandingCanvasText>

                  {secondaryText && secondaryHref ? (
                    <LandingCanvasText
                      field="secondaryLabel"
                      value={secondaryLabel ?? ""}
                      label="Вторая кнопка"
                      inline
                      hideIfEmpty
                    >
                      <Button
                        label={secondaryText}
                        variant="secondary"
                        size="lg"
                        href={secondaryHref}
                        className={landingButtonText}
                        style={styledTextStyle(secondaryLabel)}
                        icon={
                          <Icon
                            icon={researchIcons.fileText}
                            size="sm"
                            color="inherit"
                          />
                        }
                      />
                    </LandingCanvasText>
                  ) : null}
                </HStack>
              </VStack>

              <div className="relative w-full min-w-0 justify-self-stretch">
                <div className="relative aspect-[5/4] w-full min-h-[16rem] sm:min-h-[18rem] lg:min-h-[22rem]">
                  {hasImage ? (
                    <>
                      <LandingCmsImage
                        src={lightUrl}
                        alt={alt}
                        fill
                        width={880}
                        height={700}
                        priority
                        sizes="(max-width: 1024px) 100vw, 560px"
                        className={
                          hasSeparateDark
                            ? "landing-hero-split__img landing-hero-split__img--light object-contain object-center"
                            : "object-contain object-center"
                        }
                      />
                      {hasSeparateDark ? (
                        <LandingCmsImage
                          src={darkUrl}
                          alt={alt}
                          fill
                          width={880}
                          height={700}
                          priority
                          sizes="(max-width: 1024px) 100vw, 560px"
                          className="landing-hero-split__img landing-hero-split__img--dark object-contain object-center"
                        />
                      ) : null}
                    </>
                  ) : (
                    <div
                      aria-hidden
                      className="flex h-full w-full items-center justify-center rounded-sm border border-dashed border-[color-mix(in_srgb,var(--color-text-primary)_16%,transparent)] bg-[color-mix(in_srgb,var(--color-text-primary)_4%,transparent)] px-4"
                    >
                      <Text color="secondary" className="text-sm">
                        Картинка S3
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {showHighlights ? <HeroSplitHighlights items={items} /> : null}
          </div>
        </Center>
      </Section>
    </div>
  );
}
